import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { css, styled } from "styled-components";

import { ToggleableOverlay } from "common/components/Overlay";
import { zIndex } from "common/constants/zIndex";
import { breakpoints, easing } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { deactivateModal, popModalScreen } from "domains/modal/actions";
import { Modal } from "domains/modal/components/Modal";
import { modalStack } from "domains/modal/modalStack";
import { getActiveModalId, getActiveModals, getModalParams } from "domains/modal/selectors";
import type { ModalContentType, ModalParams } from "domains/modal/types";

const ROUTE_INDEPENDANT_MODALS: ModalContentType[] = [
  "Error",
  "Notifications",
  "OpenLinkInvitation",
  "Unauthenticated",
  "WhatsNew",
];

export const ModalProvider = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const activeModalId = useSelector(getActiveModalId);
  const modals = useSelector(getActiveModals);
  const params = useSelector(getModalParams) as ModalParams<ModalContentType>;
  const [isActive, setIsActive] = useState<boolean>(false);

  const location = useLocation();
  const routeOnOpen = useRef<string>(null);

  const dismiss = useCallback(() => {
    dispatch(params.popOnClose ? popModalScreen() : deactivateModal());
    if (params.onDismissRoute !== undefined) go(params.onDismissRoute);
  }, [dispatch, go, params.onDismissRoute, params.popOnClose]);

  useEffect(() => {
    if (activeModalId) {
      if (ROUTE_INDEPENDANT_MODALS.includes(activeModalId)) return;

      if (activeModalId && !routeOnOpen.current) {
        routeOnOpen.current = location.pathname;
        return;
      }
    } else {
      if (!activeModalId && routeOnOpen.current) {
        routeOnOpen.current = null;
        return;
      }
    }

    if (routeOnOpen.current !== null && routeOnOpen.current !== location.pathname) {
      dismiss();
      routeOnOpen.current = null;
    }
  }, [activeModalId, dismiss, location.pathname]);

  useEffect(() => void requestAnimationFrame(() => setIsActive(!!activeModalId)), [activeModalId]);

  return (
    <>
      <ToggleableOverlay isActive={isActive} onClick={dismiss} />
      <StyledModal isActive={isActive}>
        <StyledScrollContainer>
          <StyledInnerContent numberOfItems={modals.length}>
            {modals.map((modal) => {
              const ModalComponent = modalStack[modal.id];
              return (
                <StyledScreen key={modal.id} isActive={modal.id === activeModalId}>
                  <Modal dismiss={dismiss}>
                    <ModalComponent />
                  </Modal>
                </StyledScreen>
              );
            })}
          </StyledInnerContent>
        </StyledScrollContainer>
      </StyledModal>
    </>
  );
};

const StyledModal = styled.div<{ isActive: boolean }>`
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  bottom: 0;
  color: ${({ theme }) => theme.colors.fillPrimary};
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow-x: hidden;
  position: fixed;
  transform: translateY(${({ isActive }) => (isActive ? 0 : 100)}%);
  transition: transform 0.3s ${easing.slightBounce};
  width: 100%;
  z-index: ${zIndex.modal};

  @media ${breakpoints.large} {
    height: 100%;
    overflow-y: hidden;
    right: 0;
    top: 0;
    transform: translateX(${({ isActive }) => (isActive ? 0 : 100)}%);
    transition: transform 0.3s;
    width: 550px;
  }
`;

const StyledScrollContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
`;

const StyledInnerContent = styled.div<{ numberOfItems: number }>`
  display: flex;
  width: 100%;
  height: 100%;
  transition:
    transform 0.3s,
    opacity 0.3s;
  ${({ numberOfItems }) => css`
    transform: translate(${Math.max(0, numberOfItems - 1) * -100}%, 0);
  `}
`;

const StyledScreen = styled.div<{ isActive: boolean }>`
  height: 100%;
  min-height: 100%;
  min-width: 100%;
  width: 100%;

  ${({ isActive }) => css`
    transition:
      opacity 0.3s,
      height 0.3s;
    opacity: ${isActive ? 1 : 0};
    ${!isActive &&
    css`
      overflow-y: hidden;
      height: 0px;
    `}
  `};
`;
